import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';

import { Botao, Card, Carregando, ErroInline, Segmented, Tag, TituloTela } from '../components/Sga';
import { agruparConsecutivos } from '../lib/agrupar';
import { api, urlExportacao } from '../lib/api';
import { ddmmHora, duracao, hhmmss, kindDoNivel, n, rotuloNivel, telefone } from '../lib/format';
import type { LogIntegracao, NivelLog } from '../lib/types';

type Filtro = 'todos' | NivelLog;

interface Resposta {
  registros: LogIntegracao[];
  total: number;
  por_nivel: { nivel: NivelLog; total: number }[];
}

export function Logs({ periodo, conexaoId }: { periodo: string; conexaoId?: string | null }) {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);

    // A busca dispara com atraso: digitar não pode virar uma chamada por tecla.
    const t = window.setTimeout(() => {
      api
        .get<Resposta>('/logs', {
          nivel: filtro === 'todos' ? undefined : filtro,
          busca: busca.trim() || undefined,
          conexao_id: conexaoId ?? undefined,
          limite: 200,
        })
        .then((d) => {
          if (cancelado) return;
          setDados(d);
          setErro(null);
        })
        .catch((e) => !cancelado && setErro((e as Error).message))
        .finally(() => !cancelado && setCarregando(false));
    }, 300);

    return () => {
      cancelado = true;
      window.clearTimeout(t);
    };
  }, [filtro, busca, conexaoId]);

  const contagem = (n: NivelLog) => dados?.por_nivel.find((p) => p.nivel === n)?.total ?? 0;

  // Um rate limit disparando seis vezes não vale seis linhas. Linhas vizinhas
  // com o mesmo evento, nível, origem e conexão viram uma, com a contagem.
  const linhas = agruparConsecutivos(
    dados?.registros ?? [],
    (r) => `${r.nivel}|${r.origem}|${r.evento}|${r.codigo ?? ''}|${r.connection_id ?? ''}`,
    (r) => r.created_at,
  );

  return (
    <div className="tela-fixa">
      <TituloTela icone="mdi:text-box-search-outline" titulo="Logs de integração" contexto={periodo} />

      <Card
        className="card-fixo tela-fixa__corpo"
        acoes={
          <>
            <div className="input-group" style={{ width: 280 }}>
              <input
                className="input"
                placeholder="Buscar evento, destino ou código..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
              <span className="addon">
                <Icon icon="mdi:magnify" width={16} />
              </span>
            </div>
            <Segmented
              valor={filtro}
              onChange={setFiltro}
              opcoes={[
                { valor: 'todos', rotulo: 'Todos' },
                { valor: 'erro', rotulo: `Erros${contagem('erro') ? ` (${contagem('erro')})` : ''}` },
                { valor: 'aviso', rotulo: `Avisos${contagem('aviso') ? ` (${contagem('aviso')})` : ''}` },
                {
                  valor: 'banimento',
                  rotulo: `Banimentos${contagem('banimento') ? ` (${contagem('banimento')})` : ''}`,
                },
              ]}
            />
            <Botao
              icone="grommet-icons:power-reset"
              onClick={() => {
                setFiltro('todos');
                setBusca('');
              }}
            >
              Resetar filtros
            </Botao>
            <a
              className="btn btn-warning"
              href={urlExportacao('/logs/export', {
                nivel: filtro === 'todos' ? undefined : filtro,
                busca: busca.trim() || undefined,
              })}
              download
            >
              <Icon icon="mdi:content-save-outline" width={16} />
              Exportar CSV
            </a>
          </>
        }
        titulo="Registros"
      >
        {erro && <ErroInline mensagem={erro} />}

        <div className="sga-tabela-scroll card-fixo__grid">
          <table className="sga-table sga-table--densa" style={{ minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={{ width: 150 }}>Data e hora</th>
                <th style={{ width: 110 }}>Nível</th>
                <th style={{ width: 130 }}>Origem</th>
                <th style={{ width: 190 }}>Conexão</th>
                <th>Evento</th>
                <th style={{ width: 170 }}>Destino</th>
                <th style={{ width: 110 }}>Código</th>
                <th style={{ width: 90, textAlign: 'right' }}>Duração</th>
              </tr>
            </thead>
            <tbody>
              {carregando && !dados && (
                <tr>
                  <td colSpan={8}>
                    <Carregando />
                  </td>
                </tr>
              )}
              {dados?.registros.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="sga-vazio">
                      <Icon icon="mdi:text-box-search-outline" width={24} />
                      Nenhum registro para este filtro.
                    </div>
                  </td>
                </tr>
              )}
              {linhas.map(({ item: r, vezes, primeiro }) => (
                <tr key={r.id}>
                  <td className="mono" title={vezes > 1 && primeiro ? `Mais antigo do grupo: ${ddmmHora(primeiro)}` : undefined}>
                    {ddmmHora(r.created_at)}
                    {vezes > 1 && primeiro && (
                      <span style={{ color: 'var(--sga-fg-3-texto)' }}> ← {hhmmss(primeiro)}</span>
                    )}
                  </td>
                  <td>
                    <Tag kind={kindDoNivel(r.nivel)}>{rotuloNivel[r.nivel]}</Tag>
                  </td>
                  <td>{r.origem}</td>
                  <td data-zero={r.conexao_nome ? undefined : 'true'}>
                    {r.conexao_nome ?? '—'}
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                      {r.evento}
                      {vezes > 1 && (
                        <span className="sga-repeticao" title={`${vezes} ocorrências consecutivas`}>
                          ×{vezes}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="mono" data-zero={r.destino ? undefined : 'true'}>
                    {r.destino ? telefone(r.destino) : '—'}
                  </td>
                  <td className="mono" data-zero={r.codigo ? undefined : 'true'}>
                    {r.codigo ?? '—'}
                  </td>
                  <td className="num" data-zero={r.duracao_ms ? undefined : 'true'}>
                    {duracao(r.duracao_ms)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className="card-fixo__rodape"
          style={{ display: 'flex', fontSize: 11.5, color: 'var(--sga-fg-3)', paddingTop: 8 }}
        >
          <span>
            {n(dados?.total ?? 0)} registros
            {linhas.length < (dados?.registros.length ?? 0) &&
              ` · ${linhas.length} linhas (repetições agrupadas)`}
          </span>
          <span style={{ flex: 1 }} />
          <span>Políticas de privacidade</span>
        </div>
      </Card>
    </div>
  );
}
